import { createAsyncThunk } from "@reduxjs/toolkit";
import { displayAlert } from "../slice/alertSlice.tsx";
import companyApi from "../api/companyApi.tsx";
import {
  CompanyBodyDataType,
  CreateCompanyBodyDataType,
  EditCompanyDataType,
} from "../types/companyTypes.tsx";
import { UpdateRolePayload } from "../types/companyTypes.tsx";
import { AxiosError, isAxiosError } from "axios";
import { handleThunkError } from "./handleThunkError";

export const fetchCompanySettings = createAsyncThunk(
  "company/fetchCompanySettings",
  async (_, thunkAPI) => {
    try {
      const response = await companyApi.fetchCompanySettings();
      return response.data;
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "An unexpected error occurred.");
      return thunkAPI.rejectWithValue("Failed to fetch company settings.");
    }
  },
);
export const fetchCompaniesList = createAsyncThunk(
  "company/fetchCompaniesList",
  async (_, thunkAPI) => {
    try {
      const response = await companyApi.fetchCompaniesList();
      return response.data.results;
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "An unexpected error occurred.");
      return thunkAPI.rejectWithValue("Failed to fetch companies.");
    }
  },
);

export const fetchCompanyById = createAsyncThunk(
  "company/fetchCompanyById",
  async (id: string, thunkAPI) => {
    try {
      const response = await companyApi.fetchCompanyById(id);
      return response.data;
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "An unexpected error occurred.");
      return thunkAPI.rejectWithValue("Failed to fetch company.");
    }
  },
);

export const editCompanyById = createAsyncThunk(
  "company/editCompanyById",
  async (data: EditCompanyDataType, thunkAPI) => {
    try {
      const response = await companyApi.editCompanyById(data.body);
      if (data.callback) {
        data.callback();
      }
      return response.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429)
        return thunkAPI.rejectWithValue("Rate limited.");
      if (e instanceof AxiosError && e.response?.data?.name) {
        return thunkAPI.rejectWithValue(e.response?.data?.name.join());
      } else {
        thunkAPI.dispatch(
          displayAlert({ type: "error", text: "Failed to edit company." }),
        );
      }
    }
  },
);

export const setCompanySettings = createAsyncThunk(
  "company/setCompanySettings",
  async (data: CompanyBodyDataType, thunkAPI) => {
    try {
      const response = await companyApi.setCompanySettings(data.body);
      if (data.callback) {
        data.callback();
      }
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Company name was changed successfully",
        }),
      );
      return response.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429)
        return thunkAPI.rejectWithValue("Rate limited.");
      if (e instanceof AxiosError && e.response?.data?.name) {
        return thunkAPI.rejectWithValue(e.response?.data?.name.join());
      } else {
        thunkAPI.dispatch(
          displayAlert({ type: "error", text: "Failed to edit company." }),
        );
      }
    }
  },
);

export const createCompany = createAsyncThunk(
  "company/createCompany",
  async (data: CreateCompanyBodyDataType, thunkAPI) => {
    try {
      const response = await companyApi.createCompany(data.body);
      thunkAPI.dispatch(fetchCompaniesList());
      if (data.callback) {
        data.callback();
      }
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Company was created successfully",
        }),
      );
      return response.data;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 429)
        return thunkAPI.rejectWithValue("Rate limited.");
      if (e instanceof AxiosError && e.response?.data?.company_name) {
        return thunkAPI.rejectWithValue(e.response?.data?.company_name.join());
      } else {
        thunkAPI.dispatch(
          displayAlert({ type: "error", text: "Failed to create company." }),
        );
      }
    }
  },
);

export const fetchCompanyUsersThunk = createAsyncThunk(
  "company/fetchUsers",
  async (_, thunkAPI) => {
    try {
      const response = await companyApi.fetchCompanyUsers();
      const responseData = response.data;

      if (Array.isArray(responseData) && responseData.length > 0) {
        return responseData[0];
      }

      if (
        responseData &&
        typeof responseData === "object" &&
        !Array.isArray(responseData) &&
        ("company_members" in responseData ||
          "pending_invitations" in responseData)
      ) {
        return responseData;
      }
      return { company_members: [], pending_invitations: [] };
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "Failed to fetch company users.");
      return thunkAPI.rejectWithValue("Failed to fetch company users.");
    }
  },
);

export const inviteUserThunk = createAsyncThunk(
  "company/inviteUser",
  async (payload: { emails: string[]; role: string }, thunkAPI) => {
    try {
      // Loop through each email and send a separate invitation
      const invitePromises = payload.emails.map((email) =>
        companyApi.inviteUser({ email, role: payload.role }),
      );

      await Promise.all(invitePromises);

      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: `Successfully sent ${payload.emails.length} invitation(s).`,
        }),
      );

      // After sending, refetch the list of users to include the new pending invitations
      thunkAPI.dispatch(fetchCompanyUsersThunk());
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "Failed to send invitation(s).");
      return thunkAPI.rejectWithValue("Failed to send invitation(s).");
    }
  },
);

export const deleteInvitationThunk = createAsyncThunk(
  "company/deleteInvitation",
  async (invitationId: number, thunkAPI) => {
    try {
      await companyApi.deleteInvitation(invitationId);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Invitation successfully revoked.",
        }),
      );
      return invitationId;
    } catch (e: unknown) {
      handleThunkError(e, thunkAPI.dispatch, "Failed to revoke invitation.");
      return thunkAPI.rejectWithValue("Failed to revoke invitation.");
    }
  },
);

export const resendInvitationThunk = createAsyncThunk(
  "company/resendInvitation",
  async (payload: { companyId: number; invitationId: number }, thunkAPI) => {
    try {
      await companyApi.resendInvitation(
        payload.companyId,
        payload.invitationId,
      );
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "Invitation email sent successfully.",
        }),
      );
      thunkAPI.dispatch(fetchCompanyById(payload.companyId.toString()));
    } catch (e: unknown) {
      const error = e as AxiosError<{ error: string }>;
      const errorMessage =
        error.response?.data?.error || "Failed to send invitation email.";
      thunkAPI.dispatch(displayAlert({ type: "error", text: errorMessage }));
      return thunkAPI.rejectWithValue(errorMessage);
    }
  },
);

export const updateUserRoleThunk = createAsyncThunk(
  "company/updateUserRole",
  async (
    payload: {
      id: number;
      role: string;
      itemType: "member" | "invitation";
    },
    thunkAPI,
  ) => {
    try {
      let response;
      const rolePayload: UpdateRolePayload = { role: payload.role };

      if (payload.itemType === "member") {
        response = await companyApi.updateProfileRole(payload.id, rolePayload);
      } else {
        response = await companyApi.updateInvitationRole(
          payload.id,
          rolePayload,
        );
      }

      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          text: "User role updated successfully.",
        }),
      );
      thunkAPI.dispatch(fetchCompanyUsersThunk());
      return response.data;
    } catch (e: unknown) {
      const error = e as AxiosError<{ detail: string }>;
      const errorMessage =
        error.response?.data?.detail || "Failed to update role.";
      thunkAPI.dispatch(displayAlert({ type: "error", text: errorMessage }));
      return thunkAPI.rejectWithValue(errorMessage);
    }
  },
);
